import React, { useMemo } from 'react';

import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Divider,
  List,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import ExpandableTypography from 'src/common/components/ExpandableTypography';
import ExportPDFButton from 'src/common/components/ExportPDFButton';
import TextViewableInPopup from 'src/common/components/TextViewableInPopup';
import { Pages } from 'src/common/constants/Pages';
import { EditNoteParams } from 'src/florence/types/RouterState';

import { useGetSingleResultQuery } from '../../../common/apis/AdminApi';
import { ReactComponent as ChevronLeftIconBlue } from '../../../common/assets/icons/chevron_left_icon_blue.svg';
import { ReactComponent as EditIcon } from '../../../common/assets/icons/edit_icon.svg';

import './ResultDetailsScreen.scss';
import { FlorencePAGES } from '../../../common/components/tfa-auth/constants/pages';
import { theme } from '../../../common/theme/theme';
import { SelectableCode } from '../../../common/types/Codes';
import { SheetStatus } from '../../../common/types/SaveSheet';
import { displayDateString } from '../../../common/utils/DateUtils';

import AdminScanThumbnail from './components/AdminScanThumbnail';
import AdminSearchResultCell from './components/AdminSearchResultCell';

const ResultDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isLoading } = useGetSingleResultQuery(
    { id: id ?? '-1' },
    { refetchOnMountOrArgChange: true }
  );

  const [showAllSavedCodes, setShowAllSavedCodes] = React.useState(false);
  const [showAllFoundCodes, setShowAllFoundCodes] = React.useState(false);

  const getBackgroundClass = (status: SheetStatus | 'search' | undefined) => {
    if (status === SheetStatus.UPLOADED) return 'rgba(82, 86, 98, 0.2)';
    if (status === SheetStatus.PROCESSED) return 'rgba(38, 187, 255, 0.3)';
    if (status === SheetStatus.DRAFT) return 'rgba(241, 157, 96, 0.3)';
    if (status === SheetStatus.FINALIZED) return 'rgba(109, 223, 198, 0.3)';

    return 'rgba(34, 123, 239, 0.3)';
  };

  const getSavedCodes = useMemo(() => {
    if (!data?.sheet?.sheetCodes) return [];

    if (showAllSavedCodes) {
      return data?.sheet?.sheetCodes;
    } else {
      return data?.sheet?.sheetCodes.slice(0, 10);
    }
  }, [data, showAllSavedCodes]);

  const getFoundCodes = useMemo(() => {
    if (!data?.results) return [];

    if (showAllFoundCodes) {
      return data?.results;
    } else {
      return data?.results.slice(0, 10);
    }
  }, [data, showAllFoundCodes]);

  if (isLoading) {
    return (
      <Box className='result-details-content-wrapper'>
        <CircularProgress
          color='primary'
          size='48px'
          style={{ alignSelf: 'center', justifySelf: 'center', marginTop: 64 }}
        />
      </Box>
    );
  }

  if (!data || !id) {
    return (
      <Box className='center' sx={{ textAlign: 'center', marginTop: 40 }}>
        <Typography variant='h4'>Result does not exist.</Typography>
      </Box>
    );
  }

  const onClickEditInApp = () => {
    if (!data.sheet) return;

    navigate(
      data.sheet.clinicNote
        ? FlorencePAGES.editSheet
        : FlorencePAGES.editOPNote,
      {
        state: {
          note: data.sheet,
        } as EditNoteParams,
      }
    );
  };

  return (
    <Box className='result-details-content-wrapper white-background min-height-100'>
      <Button
        disabled={isLoading}
        sx={{
          marginBottom: '32px',
        }}
        onClick={() =>
          window.history.state && window.history.state.idx > 0
            ? navigate(-1)
            : navigate(Pages.results)
        }
        color={'primary'}
        variant={'text'}
        startIcon={<ChevronLeftIconBlue />}
      >
        Back
      </Button>

      <Typography
        fontWeight={500}
        color={theme.palette.text.secondary}
        fontSize={16}
      >
        ID: {data.id}
      </Typography>
      {data.organization && (
        <Typography
          fontWeight={500}
          color={theme.palette.text.secondary}
          fontSize={16}
          style={{
            marginTop: '8px',
            marginBottom: '8px',
          }}
        >
          Organization: {data.organization.name}
        </Typography>
      )}
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '8px',
            marginBottom: '8px',
          }}
        >
          {data.sheet?.documentName && data.sheet.documentName.length && (
            <Typography
              fontWeight={500}
              color='black'
              fontSize={16}
              style={{
                marginRight: '8px',
              }}
            >
              {data.sheet.documentName}
            </Typography>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              className='status-bubble-container'
              style={{
                backgroundColor: getBackgroundClass(
                  data.sheet?.status as SheetStatus
                ),
              }}
            >
              <Typography fontSize={16} color='black' fontWeight={500}>
                {data.sheet?.status ?? 'Search'}
              </Typography>
            </div>
          </div>
        </Box>

        {data?.sheet && (
          <Box
            style={{
              marginTop: '4px',
              marginBottom: '4px',
            }}
          >
            {data?.sheet &&
              (data.sheet.clinicNote ||
                data.sheet.status === SheetStatus.FINALIZED) && (
                <ExportPDFButton isDisabled={isLoading} note={data.sheet} />
              )}

            {data.sheet && (
              <Button
                disabled={isLoading}
                variant='contained'
                startIcon={<EditIcon fill='white' />}
                sx={{
                  borderRadius: '50px',
                  width: '146px',
                  height: '50px',
                  mx: '8px',
                  my: '4px',
                }}
                onClick={onClickEditInApp}
                disableElevation
              >
                <Typography color='white'>Edit in app</Typography>
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Divider />

      <div
        style={{
          backgroundColor: theme.palette.divider,
          width: '100%',
          height: '1px',
          marginTop: '16px',
          marginBottom: '16px',
        }}
      />

      <Typography fontSize={16} color='black' fontWeight={500}>
        {data.user.email}
      </Typography>

      <Typography
        fontWeight={500}
        color={theme.palette.text.secondary}
        fontSize={14}
      >
        Last edited:{' '}
        {displayDateString(data.sheet?.updatedAt ?? data.createdAt, false)}
      </Typography>

      {/*CODES SAVED FOR THIS NOTE */}
      {data.sheet && (
        <Box style={{ width: '100%', marginTop: '32px' }}>
          <Typography fontSize={18} color='black' fontWeight={500}>
            {data?.sheet?.sheetCodes.length}{' '}
            {data?.sheet?.sheetCodes.length === 1
              ? 'Code Saved'
              : 'Codes Saved'}
          </Typography>
          <List style={{ marginTop: '8px' }}>
            <Box>
              {getSavedCodes.map((result) => (
                <AdminSearchResultCell
                  key={result.code}
                  code={result as SelectableCode}
                  showModifiersAndMultipliers={true}
                />
              ))}
            </Box>
          </List>
          {data?.sheet?.sheetCodes.length > 10 && (
            <Box
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                onClick={() => setShowAllSavedCodes(!showAllSavedCodes)}
                variant='text'
              >
                <Typography color='black'>
                  {showAllSavedCodes ? 'Show Fewer' : 'Show All'}
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/*INITIAL SEARCH FOUND CODES*/}
      <Box style={{ width: '100%', marginTop: '32px' }}>
        <Typography fontSize={18} color='black' fontWeight={500}>
          {data?.results.length}{' '}
          {data?.results.length === 1 ? 'Code Found' : 'Codes Found'}
        </Typography>
        <List style={{ marginTop: '8px' }}>
          <Box>
            {getFoundCodes.map((result) => (
              <AdminSearchResultCell
                key={result.code}
                code={result as SelectableCode}
                showModifiersAndMultipliers={false}
              />
            ))}
          </Box>
        </List>
        {data?.results.length > 10 && (
          <Box
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              onClick={() => setShowAllFoundCodes(!showAllFoundCodes)}
              variant='text'
            >
              <Typography color='black'>
                {showAllFoundCodes ? 'Show Fewer' : 'Show All'}
              </Typography>
            </Button>
          </Box>
        )}
      </Box>

      <Box
        style={{
          display: 'flex',
          width: '100%',
          marginTop: '32px',
          marginBottom: '64px',
        }}
      >
        <Box style={{ width: '100%' }}>
          <Typography fontSize={18} color='black' fontWeight={500}>
            Text
          </Typography>
          <Box
            sx={{
              width: '100%',
              backgroundColor: 'rgba(82, 86, 98, 0.05)',
              borderRadius: '16px',
              padding: '24px',
              marginTop: '16px',
            }}
          >
            <ExpandableTypography lineCount={24} fontSize={16}>
              {data.text}
            </ExpandableTypography>
          </Box>
        </Box>

        {data?.sheet?.scanDocumentUrl && data?.sheet?.scanDocumentUrl !== '' && (
          <Box style={{ marginTop: 48, marginLeft: 24 }}>
            <AdminScanThumbnail
              filename={data?.sheet?.scanDocumentUrl}
              userId={data.user.id}
            />
          </Box>
        )}
      </Box>
      {data?.sheet?.clinicNote && (
        <Box
          style={{
            width: '100%',
            marginBottom: '64px',
            backgroundColor: '#0D4E5C',
          }}
        >
          <Box style={{ width: '100%' }}>
            <Typography fontSize={18} color='black' fontWeight={500}>
              Clinic Engine Results
            </Typography>
            <Box
              sx={{
                width: '100%',
                backgroundColor: 'rgba(82, 86, 98, 0.05)',
                borderRadius: '16px',
                padding: '24px',
                marginTop: '16px',
              }}
            >
              <Typography fontSize={16} fontWeight={600}>
                Clinic Note Status
              </Typography>
              <Typography
                fontSize={16}
                fontWeight={500}
                sx={{ color: '#002145B2' }}
              >
                {data.sheet.clinicNoteResults?.status_response ?? 'N/A'}
              </Typography>

              <Typography
                fontSize={16}
                fontWeight={600}
                sx={{ marginTop: '24px' }}
              >
                Final Code
              </Typography>
              <Typography
                fontSize={16}
                fontWeight={500}
                sx={{ color: '#002145B2' }}
              >
                {data.sheet.clinicNoteResults?.final_code ?? 'N/A'}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gridTemplateRows: '1fr',
                  gap: '32px',
                }}
              >
                <Box>
                  <Typography
                    fontSize={16}
                    fontWeight={600}
                    sx={{ marginTop: '24px' }}
                  >
                    ICD-10 Codes
                  </Typography>

                  <TextViewableInPopup title='ICD-10 Codes'>
                    {data.sheet.clinicNoteResults?.icd_response ?? 'N/A'}
                  </TextViewableInPopup>
                </Box>

                <Box>
                  <Typography
                    fontSize={16}
                    fontWeight={600}
                    sx={{ marginTop: '24px' }}
                  >
                    CPT Codes
                  </Typography>

                  <TextViewableInPopup title='CPT Codes'>
                    {data.sheet.clinicNoteResults?.cpt_response ?? 'N/A'}
                  </TextViewableInPopup>
                </Box>

                <Box>
                  <Typography
                    fontSize={16}
                    fontWeight={600}
                    sx={{ marginTop: '24px' }}
                  >
                    HCPCS Codes
                  </Typography>

                  <TextViewableInPopup title='HCPCS Codes'>
                    {data.sheet.clinicNoteResults?.hcpc_response ?? 'N/A'}
                  </TextViewableInPopup>
                </Box>
              </Box>

              <Typography
                fontSize={16}
                fontWeight={600}
                sx={{ marginTop: '32px' }}
              >
                Problem Complexity Assessment
              </Typography>
              <ExpandableTypography lineCount={8} fontSize={16}>
                {data.sheet.clinicNoteResults?.problem_response ?? 'N/A'}
              </ExpandableTypography>

              <Typography
                fontSize={16}
                fontWeight={600}
                sx={{ marginTop: '32px' }}
              >
                Data Complexity Assessment
              </Typography>
              <ExpandableTypography lineCount={8} fontSize={16}>
                {data.sheet.clinicNoteResults?.data_response ?? 'N/A'}
              </ExpandableTypography>

              <Typography
                fontSize={16}
                fontWeight={600}
                sx={{ marginTop: '32px' }}
              >
                Risk Complexity Assessment
              </Typography>
              <ExpandableTypography lineCount={8} fontSize={16}>
                {data.sheet.clinicNoteResults?.risk_response ?? 'N/A'}
              </ExpandableTypography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ResultDetailsScreen;
